/**
 * 图片压缩工具函数
 * 将图片压缩到指定大小以内
 */

interface CompressOptions {
  maxSizeKB: number; // 最大文件大小（KB）
  maxWidth?: number; // 最大宽度
  maxHeight?: number; // 最大高度
  quality?: number; // 初始质量 (0-1)
}

export async function compressImage(
  file: File, 
  options: CompressOptions = { maxSizeKB: 30 }
): Promise<File> {
  const { maxSizeKB, maxWidth = 1920, maxHeight = 1080, quality = 0.8 } = options;
  
  return new Promise((resolve, reject) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();
    
    img.onload = () => {
      try {
        // 计算新的尺寸，保持宽高比
        let { width, height } = calculateNewDimensions(
          img.width, 
          img.height, 
          maxWidth, 
          maxHeight
        );
        
        canvas.width = width;
        canvas.height = height;
        
        if (!ctx) {
          reject(new Error('无法获取canvas上下文'));
          return;
        }
        
        // 绘制图片到canvas
        ctx.drawImage(img, 0, 0, width, height);
        
        // 压缩图片
        compressToTargetSize(canvas, file.name, maxSizeKB * 1024, quality)
          .then(resolve)
          .catch(reject);
          
      } catch (error) {
        reject(error);
      }
    };
    
    img.onerror = () => {
      reject(new Error('图片加载失败'));
    };
    
    // 创建图片URL
    const url = URL.createObjectURL(file);
    img.src = url;
    
    // 在加载完成后清理URL
    const originalOnLoad = img.onload;
    img.onload = (e) => {
      URL.revokeObjectURL(url);
      if (originalOnLoad) {
        originalOnLoad.call(img, e);
      }
    };
  });
}

/**
 * 计算新的图片尺寸，保持宽高比
 */
function calculateNewDimensions(
  originalWidth: number,
  originalHeight: number,
  maxWidth: number,
  maxHeight: number
): { width: number; height: number } {
  let width = originalWidth;
  let height = originalHeight;
  
  // 如果图片尺寸超过最大限制，按比例缩放
  if (width > maxWidth) {
    height = (height * maxWidth) / width;
    width = maxWidth;
  }
  
  if (height > maxHeight) {
    width = (width * maxHeight) / height;
    height = maxHeight;
  }
  
  return { width: Math.round(width), height: Math.round(height) };
}

/**
 * 压缩图片到目标文件大小
 */
async function compressToTargetSize(
  canvas: HTMLCanvasElement,
  fileName: string,
  targetSizeBytes: number,
  initialQuality: number = 0.8
): Promise<File> {
  let quality = initialQuality;
  let blob: Blob | null = null;
  
  // 二分法查找合适的质量值
  let minQuality = 0.1;
  let maxQuality = initialQuality;
  
  for (let i = 0; i < 10; i++) { // 最多尝试10次
    blob = await new Promise<Blob | null>((resolve) => {
      canvas.toBlob(resolve, 'image/jpeg', quality);
    });
    
    if (!blob) {
      throw new Error('图片压缩失败');
    }
    
    console.log(`压缩尝试 ${i + 1}: 质量=${quality.toFixed(2)}, 大小=${(blob.size / 1024).toFixed(1)}KB`);
    
    if (blob.size <= targetSizeBytes) {
      // 如果文件大小符合要求，尝试提高质量
      if (blob.size < targetSizeBytes * 0.8) { // 如果文件太小，尝试提高质量
        minQuality = quality;
        quality = (quality + maxQuality) / 2;
      } else {
        break; // 大小合适，退出循环
      }
    } else {
      // 文件太大，降低质量
      maxQuality = quality;
      quality = (minQuality + quality) / 2;
    }
    
    // 如果质量差异很小，退出循环
    if (maxQuality - minQuality < 0.01) {
      break;
    }
  }
  
  if (!blob) {
    throw new Error('图片压缩失败');
  }
  
  // 如果仍然太大，进一步降低尺寸
  if (blob.size > targetSizeBytes) {
    console.log('质量压缩不够，尝试降低尺寸...');
    const scaleFactor = Math.sqrt(targetSizeBytes / blob.size);
    const newWidth = Math.round(canvas.width * scaleFactor);
    const newHeight = Math.round(canvas.height * scaleFactor);
    
    // 重新绘制到更小的canvas
    const smallCanvas = document.createElement('canvas');
    const smallCtx = smallCanvas.getContext('2d');
    
    if (!smallCtx) {
      throw new Error('无法创建压缩canvas');
    }
    
    smallCanvas.width = newWidth;
    smallCanvas.height = newHeight;
    
    smallCtx.drawImage(canvas, 0, 0, newWidth, newHeight);
    
    blob = await new Promise<Blob | null>((resolve) => {
      smallCanvas.toBlob(resolve, 'image/jpeg', 0.8);
    });
    
    if (!blob) {
      throw new Error('尺寸压缩失败');
    }
  }
  
  console.log(`最终压缩结果: ${(blob.size / 1024).toFixed(1)}KB`);
  
  // 创建新的File对象
  const compressedFile = new File([blob], fileName, {
    type: 'image/jpeg',
    lastModified: Date.now(),
  });
  
  return compressedFile;
}

/**
 * 获取图片文件的尺寸信息
 */
export function getImageDimensions(file: File): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    
    img.onload = () => {
      resolve({ width: img.width, height: img.height });
      URL.revokeObjectURL(img.src);
    };
    
    img.onerror = () => {
      reject(new Error('无法读取图片尺寸'));
      URL.revokeObjectURL(img.src);
    };
    
    img.src = URL.createObjectURL(file);
  });
}